/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2023, Bulb Inc. For more information, please email contact@hellobulb.com.
 *          _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
*/
import {
  Component, EventEmitter, Inject, Input, OnInit, Output, ViewChild,
} from '@angular/core';
import { CropperComponent } from 'angular-cropperjs';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ImageAsset } from '../image/image-asset.model';

/**
 *  Enum for type of image to decide the shape of cropper.
 */
export enum ImageType {
  CIRCULAR = 'CIRCULAR',
  FREEFORM = 'FREEFORM',
  LANDSCAPE = 'LANDSCAPE',
  PORTRAIT = 'PORTRAIT',
  SQUARE = 'SQUARE',
}

/**
 * Component which contains angular cropper to crop image as per our required shape.
 *
 * The cropper is nested within a container, which we call the frame.  Within the cropper,
 * there is a canvas, which is where the image is displayed.
 * Below is the detailed usage of following component and parameters used in this class:
 * We have two parameters frameSize and imageSize.
 * 1) We make use of image Size for scaling the image height and width for canvas:
 *  example: imageSize = const maxSize = 280px
 *  const maxSize = this.imageSize ? this.imageSize : 280;
 *  if (this.imageWidth > maxSize) {
 *     const imageScale = maxSize / this.imageHeight;
 *     this.imageWidth = maxSize;
 *     this.imageHeight *= imageScale;
 *  }
 * 2) We make use of frame size for scaling the container height and width inside which image will be shown.
 *  example: frameSize = const maxSize = 465
 *  const maxSize = this.frameSize ? this.frameSize : 465;
     if (this.imageWidth > maxSize) {
      const imageScale = maxSize / this.imageWidth;
      this.imageFrameWidth = maxSize;
      this.imageFrameHeight = this.imageHeight * imageScale;
    }
 */
@Component({
  selector: 'bulb-image-cropper',
  templateUrl: './image-cropper.component.html',
  styleUrls: ['./image-cropper.component.scss'],
  standalone: false,
})
export class ImageCropperComponent implements OnInit {

  /**
   * The asset of the image to display.
   */
  @Input() imageAsset: ImageAsset;

  /**
   *  It is the type of image to decide the shape of cropper.
   */
  @Input() type: ImageType;

  /**
   * It is the max width of image cropper container in px for scaling.
   */
  @Input() frameSize: number;

  /**
   *  contains ckeditor instance and content blockId.
   */
  @Input() extra: any;

  /**
   * Variable to check rotate button should be enable or not.
   */
  @Input() enableRotate: boolean = false;

  /**
   * Indicates whether more images need to be cropped.
   */
  @Input() hasMoreImages: boolean = false;

  /**
   * EventEmitter to send the saveEvent to parent.
   */
  @Output() public saveCroppedImage: EventEmitter<any> = new EventEmitter();

  /**
   * EventEmitter to send the cancelEvent to parent.
   */
  @Output() public cancelCrop: EventEmitter<any> = new EventEmitter();

  /**
   * It is the angular cropper instance.
   */
  @ViewChild('angularCropper') public angularCropper: CropperComponent;

  /**
   *  It is the height of image in px which is shown in dialog box.
   */
  imageHeight : number;

  /**
   *  It is the width of image in px which is shown in dialog box.
   */
  imageWidth : number;

  /**
   * width of image frame in px.
   */
  imageFrameWidth: number;

  /**
   * Height of image frame in px.
   */
  imageFrameHeight: number;

  /**
   * Url of image.
   */
  imageUrl: string;

  /**
   * Configuration of cropper instance which contains scaling, canvas height and width, aspect ratio etc.
   */
  config: any;

  /**
   * Crop Modal Title.
   */
  title: string;

  constructor(
    @Inject('ASSET_LIBRARY') private ASSET_LIBRARY,
    private dynamicDialogRef: DynamicDialogRef,
    private dynamicConfig : DynamicDialogConfig,
  ) { }

  ngOnInit(): void {
    this.config = {
      viewMode: 1,
      checkCrossOrigin: false,
      guides: false,
      highlight: false,
      background: false,
      dragMode: 'move',
      rotatable: true,
      movable: true,
      zoomable: true,
      scalable: false,
      zoomOnWheel: true,
      responsive: true,
      minContainerWidth: 0,
      minContainerHeight: 0,
      minCanvasWidth: 0,
      minCanvasHeight: 0,
      minCropBoxWidth: this.ASSET_LIBRARY?.CROP?.MIN_CROP_WIDTH,
      minCropBoxHeight: this.ASSET_LIBRARY?.CROP?.MIN_CROP_HEIGHT,
    };
    this.initData();
    this.setFrameSize();
    this.updateAspectRatio(this.type);
    this.imageHeight = this.imageAsset?.height;
    this.imageWidth = this.imageAsset?.width;
    this.imageUrl = this.imageAsset?.url;
    this.setSize();
  }

  /**
   * Set image height and width to fit in modal.
   */
  setSize() {
    const maxSize = 300;
    if (this.config.aspectRatio >= 1) {
      if (this.imageWidth > maxSize) {
        const imageScale = maxSize / this.imageWidth;
        this.imageWidth = maxSize;
        this.imageHeight *= imageScale;
      }
    } else if ((this.imageHeight > this.imageWidth) && (this.imageHeight > maxSize)) {
      const imageScale = maxSize / this.imageHeight;
      this.imageWidth *= imageScale;
      this.imageHeight = maxSize;
    } else if ((this.imageWidth > this.imageHeight) && (this.imageWidth > maxSize)) {
      const imageScale = maxSize / this.imageWidth;
      this.imageWidth = maxSize;
      this.imageHeight *= imageScale;
    }
    this.config.minContainerWidth = this.imageWidth;
    this.config.minContainerHeight = this.imageHeight;
    this.config.minCanvasWidth = this.imageWidth;
    this.config.minCanvasHeight = this.imageHeight;
  }

  /**
   * It rotates image by 90 degree whenever it gets called.
   */
  rotate() {
    this.angularCropper.cropper.rotate(90);
  }

  /**
   * Add cropper classes on image load.
   */
  imageLoaded() {
    const style = {
      width: this.imageAsset.width,
      height: this.imageAsset.height,
      'max-width': this.imageAsset.width,
      'max-height': this.imageAsset.height,
    };

    if (this.type === ImageType.CIRCULAR) {
      const cropperBox = document.querySelectorAll('.cropper-crop-box');
      cropperBox[0].classList.add('cropper-crop-box-profile');
      const cropperBoxView = document.querySelectorAll('.cropper-view-box');
      cropperBoxView[0].classList.add('cropper-crop-box-profile', 'cropper-view-box-profile');
    }
  }

  /**
   *  Set frame size.
   */
  setFrameSize() {
    const maxSize = this.frameSize ? this.frameSize : 465;
    if (this.imageWidth > maxSize) {
      const imageScale = maxSize / this.imageWidth;
      this.imageFrameWidth = maxSize;
      this.imageFrameHeight = this.imageHeight * imageScale;
    } else {
      this.imageFrameWidth = this.imageWidth;
      this.imageFrameHeight = this.imageHeight;
    }
  }

  /**
   * Method dispatches the cropped data of image.
   */
  cropped() {
    if (!this.hasMoreImages) {
      this.closeDialog();
    }
    const croppedData = this.angularCropper?.cropper?.getData(true);
    const data = {
      imageAsset: this.imageAsset,
      croppedData,
      extra: this.extra,
    };

    return this.saveCroppedImage.emit(data);
  }

  /**
   * Method to close the crop dialog option and dispatches the cancel event.
   */
  cancel() {
    this.closeDialog();
    this.cancelCrop?.emit();
  }

  /**
   *  Assign aspect ratio according to orientation.
   * @param orientation contains orientation selected by user.
   */
  updateAspectRatio(orientation: ImageType) {
    if (orientation === 'LANDSCAPE') {
      this.config.aspectRatio = 16 / 9;
    } else if (orientation === 'PORTRAIT') {
      this.config.aspectRatio = 3 / 4;
    } else if (orientation === 'SQUARE' || orientation === ImageType.CIRCULAR) {
      this.config.aspectRatio = 1;
    }
  }

  /**
   * Will close the dialog.
   */
  closeDialog() {
    /*
    Important Note:
    This is needed to remove primeng dynamic dialog from the DOM. For some reason, the component that
    registers the initial opening of the dialog can't seem to close it. This only happens in our AngularJS
    framework.
    */
    document.getElementsByTagName('p-dynamicdialog')[0].remove();
    this.dynamicDialogRef.close();
  }

  /**
   * Initialize data when cropper called from service.
   */
  initData() {
    if (this.dynamicConfig.data?.isFromService) {
      this.enableRotate = this.dynamicConfig.data?.rotate;
      this.imageAsset = this.dynamicConfig.data?.image;
      this.frameSize = this.dynamicConfig.data?.frameSize;
      this.type = this.dynamicConfig.data?.type;
      this.extra = this.dynamicConfig.data?.extra;
      this.title = this.dynamicConfig.data?.title;
      this.saveCroppedImage = this.dynamicConfig.data?.onSave;
    }
  }

}
